/**
 * Deterministic randomness for the domain (content-schemas §14.0). `Math.random` is banned in core/.
 * mulberry32 keeps its whole state in one uint32, which is what the save stores per stream.
 */
export interface Rng {
  /** [0, 1) */
  next(): number;
  /** Inclusive integer range. */
  int(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  weighted<T>(items: readonly T[], weight: (item: T) => number): T;
  /** Current state, to persist after use. */
  readonly state: number;
}

export function createRng(seed: number): Rng {
  let s = seed >>> 0;
  const next = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    pick: (items) => {
      if (items.length === 0) throw new RangeError('pick from empty list');
      return items[Math.floor(next() * items.length)] as (typeof items)[number];
    },
    weighted: (items, weight) => {
      const total = items.reduce((sum, item) => sum + Math.max(0, weight(item)), 0);
      if (items.length === 0 || total <= 0) throw new RangeError('weighted pick needs a positive total');
      let r = next() * total;
      for (const item of items) {
        r -= Math.max(0, weight(item));
        if (r < 0) return item;
      }
      return items[items.length - 1] as (typeof items)[number];
    },
    get state() {
      return s;
    },
  };
}

/** Stable 32-bit hash for deriving per-stream seeds: hash32(save.rngSeed, 'dialogue', counter). */
export function hash32(...parts: readonly (string | number)[]): number {
  let h = 0x811c9dc5;
  for (const part of parts) {
    const text = `${String(part)}|`;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
  }
  return h >>> 0;
}
